/**
 * 메인 App 컴포넌트
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import ProtectedRoute from './routes/ProtectedRoute';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import BookingsList from './pages/bookings/BookingsList';
import BookingDetail from './pages/bookings/BookingDetail';
import TicketsList from './pages/tickets/TicketsList';
import TicketDetail from './pages/tickets/TicketDetail';
import PaymentsList from './pages/payments/PaymentsList';
import PaymentDetail from './pages/payments/PaymentDetail';
import VouchersList from './pages/vouchers/VouchersList';
import VoucherDetail from './pages/vouchers/VoucherDetail';
import HallsList from './pages/products/halls/HallsList';
import HallDetail from './pages/products/halls/HallDetail';
import PackagesList from './pages/products/packages/PackagesList';
import PricesList from './pages/products/prices/PricesList';
import AgentsList from './pages/agents/AgentsList';
import PartnersList from './pages/partners/PartnersList';
import PartnerDetail from './pages/partners/PartnerDetail';
import PartnerForm from './pages/partners/PartnerForm';
import NoticesList from './pages/notices/NoticesList';
import NoticeDetail from './pages/notices/NoticeDetail';
import NoticeForm from './pages/notices/NoticeForm';
import FAQsList from './pages/faqs/FAQsList';
import FAQDetail from './pages/faqs/FAQDetail';
import FAQForm from './pages/faqs/FAQForm';
import PromotionsList from './pages/promotions/PromotionsList';
import PromotionDetail from './pages/promotions/PromotionDetail';
import EventsList from './pages/events/EventsList';
import EventDetail from './pages/events/EventDetail';
import EventForm from './pages/events/EventForm';
import InquiriesList from './pages/inquiries/InquiriesList';
import InquiryDetail from './pages/inquiries/InquiryDetail';
import InquiryStats from './pages/inquiries/InquiryStats';
import AdminsList from './pages/admins/AdminsList';
import RoleGroupsList from './pages/admins/RoleGroupsList';
import MenuPermissions from './pages/admins/MenuPermissions';
import Menus from './pages/admins/Menus';
import DataAccessPermissions from './pages/admins/DataAccessPermissions';
import FnbVendorsList from './pages/fnb/FnbVendorsList';
import FnbVendorDetail from './pages/fnb/FnbVendorDetail';
import FnbVendorForm from './pages/fnb/FnbVendorForm';
import PosDevicesList from './pages/pos/PosDevicesList';
import PosDeviceDetail from './pages/pos/PosDeviceDetail';
import PosDeviceForm from './pages/pos/PosDeviceForm';
import CctvDevicesList from './pages/cctv/CctvDevicesList';
import CctvDeviceDetail from './pages/cctv/CctvDeviceDetail';
import CctvDeviceForm from './pages/cctv/CctvDeviceForm';
import PgProvidersList from './pages/pg/PgProvidersList';
import PgProviderDetail from './pages/pg/PgProviderDetail';
import PgProviderForm from './pages/pg/PgProviderForm';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PermissionProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bookings"
                element={
                  <ProtectedRoute>
                    <BookingsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bookings/:id"
                element={
                  <ProtectedRoute>
                    <BookingDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tickets"
                element={
                  <ProtectedRoute>
                    <TicketsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tickets/:id"
                element={
                  <ProtectedRoute>
                    <TicketDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <ProtectedRoute>
                    <PaymentsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payments/:id"
                element={
                  <ProtectedRoute>
                    <PaymentDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/vouchers"
                element={
                  <ProtectedRoute>
                    <VouchersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/vouchers/:id"
                element={
                  <ProtectedRoute>
                    <VoucherDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/products/halls"
                element={
                  <ProtectedRoute>
                    <HallsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/products/halls/:id"
                element={
                  <ProtectedRoute>
                    <HallDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/products/packages"
                element={
                  <ProtectedRoute>
                    <PackagesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/products/prices"
                element={
                  <ProtectedRoute>
                    <PricesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/agents"
                element={
                  <ProtectedRoute>
                    <AgentsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/partners"
                element={
                  <ProtectedRoute>
                    <PartnersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/partners/new"
                element={
                  <ProtectedRoute>
                    <PartnerForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/partners/:id/edit"
                element={
                  <ProtectedRoute>
                    <PartnerForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/partners/:id"
                element={
                  <ProtectedRoute>
                    <PartnerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notices"
                element={
                  <ProtectedRoute>
                    <NoticesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notices/new"
                element={
                  <ProtectedRoute>
                    <NoticeForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notices/:id/edit"
                element={
                  <ProtectedRoute>
                    <NoticeForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notices/:id"
                element={
                  <ProtectedRoute>
                    <NoticeDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/faqs"
                element={
                  <ProtectedRoute>
                    <FAQsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/faqs/new"
                element={
                  <ProtectedRoute>
                    <FAQForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/faqs/:id/edit"
                element={
                  <ProtectedRoute>
                    <FAQForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/faqs/:id"
                element={
                  <ProtectedRoute>
                    <FAQDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/promotions"
                element={
                  <ProtectedRoute>
                    <PromotionsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/promotions/:id"
                element={
                  <ProtectedRoute>
                    <PromotionDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/events"
                element={
                  <ProtectedRoute>
                    <EventsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/events/new"
                element={
                  <ProtectedRoute>
                    <EventForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/events/:id/edit"
                element={
                  <ProtectedRoute>
                    <EventForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/events/:id"
                element={
                  <ProtectedRoute>
                    <EventDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/inquiries"
                element={
                  <ProtectedRoute>
                    <InquiriesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/inquiries/stats"
                element={
                  <ProtectedRoute>
                    <InquiryStats />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/inquiries/:id"
                element={
                  <ProtectedRoute>
                    <InquiryDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/admins"
                element={
                  <ProtectedRoute>
                    <AdminsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/admins/role-groups"
                element={
                  <ProtectedRoute>
                    <RoleGroupsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/admins/permissions"
                element={
                  <ProtectedRoute>
                    <MenuPermissions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/menus"
                element={
                  <ProtectedRoute>
                    <Menus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/menu-permissions"
                element={
                  <ProtectedRoute>
                    <MenuPermissions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/admins/data-access-permissions"
                element={
                  <ProtectedRoute>
                    <DataAccessPermissions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/fnb/vendors"
                element={
                  <ProtectedRoute>
                    <FnbVendorsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/fnb/vendors/new"
                element={
                  <ProtectedRoute>
                    <FnbVendorForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/fnb/vendors/:id/edit"
                element={
                  <ProtectedRoute>
                    <FnbVendorForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/fnb/vendors/:id"
                element={
                  <ProtectedRoute>
                    <FnbVendorDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pos/devices"
                element={
                  <ProtectedRoute>
                    <PosDevicesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pos/devices/new"
                element={
                  <ProtectedRoute>
                    <PosDeviceForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pos/devices/:id/edit"
                element={
                  <ProtectedRoute>
                    <PosDeviceForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pos/devices/:id"
                element={
                  <ProtectedRoute>
                    <PosDeviceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/cctv/devices"
                element={
                  <ProtectedRoute>
                    <CctvDevicesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/cctv/devices/new"
                element={
                  <ProtectedRoute>
                    <CctvDeviceForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/cctv/devices/:id/edit"
                element={
                  <ProtectedRoute>
                    <CctvDeviceForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/cctv/devices/:id"
                element={
                  <ProtectedRoute>
                    <CctvDeviceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pg/providers"
                element={
                  <ProtectedRoute>
                    <PgProvidersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pg/providers/new"
                element={
                  <ProtectedRoute>
                    <PgProviderForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pg/providers/:id/edit"
                element={
                  <ProtectedRoute>
                    <PgProviderForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pg/providers/:id"
                element={
                  <ProtectedRoute>
                    <PgProviderDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </PermissionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
